/**
 * Клиентский расчёт авторских листов и числа страниц для PDF/DOCX.
 * Общая логика трекера (попап файлов, создание проекта, mini-app).
 * Тяжёлый код в `authorSheetsCalculatorCore.ts` подгружается лениво.
 */

const PDF_MIME = 'application/pdf';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function isSupportedProjectMetricsFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (file.type === PDF_MIME || name.endsWith('.pdf')) return true;
  if (file.type === DOCX_MIME || name.endsWith('.docx')) return true;
  return false;
}

export type ClientFileMetrics = {
  authorSheets: number;
  pages: number;
  characters: number;
};

export type ClientFileMetricsTryResult =
  | { ok: true; metrics: ClientFileMetrics }
  | { ok: false; errorMessage: string };

/** Отображение АЛ: ровно один знак после запятой (N.N). */
export function formatAuthorSheetsOneDecimal(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return 'N/A';
  return n.toFixed(1);
}

export async function tryComputeAuthorSheetsAndPagesFromFile(file: File): Promise<ClientFileMetricsTryResult> {
  if (!isSupportedProjectMetricsFile(file)) {
    return { ok: false, errorMessage: 'Поддерживаются только PDF и DOCX' };
  }
  try {
    const { computeAuthorSheetsAndPagesFromFileImpl } = await import('./authorSheetsCalculatorCore');
    const metrics = await computeAuthorSheetsAndPagesFromFileImpl(file);
    return { ok: true, metrics };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Не удалось вычислить метрики файла';
    return { ok: false, errorMessage: msg };
  }
}

export async function computeAuthorSheetsAndPagesFromFile(file: File): Promise<ClientFileMetrics> {
  const r = await tryComputeAuthorSheetsAndPagesFromFile(file);
  if (!r.ok) throw new Error(r.errorMessage);
  return r.metrics;
}
